import React, { FunctionComponent } from "react";
import { V2Question } from "../helper";
import { CellValue, GridAnswers, Learner, displayName } from "./types";
import QuestionHeaderCell from "./QuestionHeaderCell";
import { NAME_COL } from "./useGridResize";
import styles from "./TabularForm.module.scss";

interface GridProps {
  questions: V2Question[];
  learners: Learner[];
  answers: GridAnswers;
  // Required cells left empty on submit — "{learnerId}:{questionId}"; outlined red until filled.
  invalidCells?: Set<string>;
  renderCell?: (question: V2Question, learner: Learner, value: CellValue, rowIndex: number, colIndex: number) => React.ReactNode;
  // Keyboard-navigation / active-cell wiring.
  isActive?: (row: number, col: number) => boolean;
  onActivate?: (row: number, col: number, focusTarget?: EventTarget | null) => void;
  registerCell?: (row: number, col: number) => (node: HTMLElement | null) => void;
  // Resize drag handles.
  onStartRowResize?: (event: React.PointerEvent) => void;
  onStartColumnResize?: (columnId: string, event: React.PointerEvent) => void;
  getColumnWidth?: (columnId: string) => number;
  srnoWidth?: number;
}

// Spreadsheet body: sticky Sr.No + Name columns and header row; a real <table> for CSS-only sticky.
const Grid: FunctionComponent<GridProps> = ({
  questions,
  learners,
  answers,
  invalidCells,
  renderCell,
  isActive,
  onActivate,
  registerCell,
  onStartRowResize,
  onStartColumnResize,
  getColumnWidth,
  srnoWidth
}) => (
  <div className={styles.scrollArea}>
    <table className={styles.grid}>
      {/* Column widths are driven here so table-layout: fixed reflows reliably on resize. */}
      <colgroup>
        <col style={{ width: srnoWidth }} />
        <col style={{ width: getColumnWidth ? getColumnWidth(NAME_COL) : undefined }} />
        {questions.map(question => (
          <col key={question.id} style={{ width: getColumnWidth ? getColumnWidth(question.id) : undefined }} />
        ))}
      </colgroup>
      <thead>
        <tr>
          <th className={`${styles.headCell} ${styles.srnoCol} ${styles.corner}`}>
            Sr. No
            {onStartRowResize && <div className={styles.rowResizeHandle} onPointerDown={onStartRowResize} title="Drag to resize row height" />}
          </th>
          <th className={`${styles.headCell} ${styles.nameCol} ${styles.corner}`}>
            Learner Name
            {onStartColumnResize && (
              <div className={styles.colResizeHandle} onPointerDown={event => onStartColumnResize(NAME_COL, event)} title="Drag to resize column" />
            )}
          </th>
          {questions.map(question => (
            // eslint-disable-next-line jsx-a11y/control-has-associated-label
            <th key={question.id} className={styles.headCell}>
              <QuestionHeaderCell question={question} />
              {onStartColumnResize && (
                <div className={styles.colResizeHandle} onPointerDown={event => onStartColumnResize(question.id, event)} title="Drag to resize column" />
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {learners.map((learner, rowIndex) => (
          <tr key={learner.userId}>
            <td className={`${styles.bodyCell} ${styles.srnoCol}`}>{rowIndex + 1}</td>
            <td className={`${styles.bodyCell} ${styles.nameCol}`} title={displayName(learner)}>
              <span className={styles.learnerName}>{displayName(learner)}</span>
            </td>
            {questions.map((question, colIndex) => {
              const value = answers[learner.userId]?.[question.id] ?? null;
              const active = isActive ? isActive(rowIndex, colIndex) : false;
              const invalid = invalidCells ? invalidCells.has(`${learner.userId}:${question.id}`) : false;
              return (
                <td key={question.id} className={styles.bodyCell}>
                  <div
                    ref={registerCell ? registerCell(rowIndex, colIndex) : undefined}
                    // tabIndex=-1 makes the container focusable so a selected cell captures arrow/Return keys.
                    tabIndex={-1}
                    className={`${styles.cellInner} ${active ? styles.activeCell : ""} ${invalid ? styles.invalidCell : ""}`}
                    data-invalid={invalid || undefined}
                    onFocusCapture={event => onActivate && onActivate(rowIndex, colIndex, event.target)}
                  >
                    {renderCell ? (
                      renderCell(question, learner, value, rowIndex, colIndex)
                    ) : (
                      <span className={styles.cellPlaceholder}>{Array.isArray(value) ? value.join(", ") : value}</span>
                    )}
                  </div>
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default Grid;
